import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import { Download } from "phosphor-react";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { MagnifyingGlass } from "@phosphor-icons/react";
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import "../Styles/addCustReport.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../Styles/controlpoints.css";

import NavSub from "../components/NavSub";
import { AuthContext } from "../context/AuthContext";
import { UserContext } from "../context/UserContext";
import { taskApiUrl } from "../utils/GetUrl";

// Register required modules
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
]);

// Utility functions
const formatDateTime = (date) => {
  const pad = (n) => (n < 10 ? "0" + n : n);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )}`;
};

const getMonthDateRange = (year, month) => {
  const start = new Date(year, month, 1, 0, 0, 0);
  const end = new Date(year, month + 1, 0, 23, 59, 59);
  return {
    startDate: formatDateTime(start),
    endDate: formatDateTime(end),
  };
};

// ---- New helpers for robust date comparisons ----
// Convert a date string into a local-date-only (midnight) Date object.
// Returns null when input invalid.
const toLocalDateOnly = (dateString) => {
  if (!dateString) return null;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// Returns true if dateString's local date is strictly before today's local date.
const isBeforeToday = (dateString) => {
  const d = toLocalDateOnly(dateString);
  if (!d) return false;
  const today = new Date();
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  return d.getTime() < todayOnly.getTime();
};

const StatusFilter = ({ value, onChange }) => (
  <select
    value={value}
    onChange={onChange}
    style={{
      padding: "8px 12px",
      fontSize: "14px",
      borderRadius: "6px",
      border: "1px solid #ccc",
      outline: "none",
      margin: "5px 0",
    }}
  >
    <option value="all">All</option>
    <option value="p">Pending</option>
    <option value="c">Completed</option>
    <option value="o">Overdue</option>
  </select>
);

const CommentDialog = ({ open, onClose, onConfirm, rowData, newStatus }) => {
  const [comment, setComment] = useState("");

  const handleClose = () => {
    setComment("");
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(comment);
    setComment("");
  };

  const getDialogTitle = () => {
    if (rowData?.Status === "Overdue" && newStatus === "Pending") {
      return `Add Remark and Extend Due Date for ${
        rowData?.controlPointDetails?.controlPoint || "Task"
      }`;
    } else if (rowData?.Status === "Overdue" && newStatus === "Completed") {
      return `Add Remark for Completing Overdue Task - ${
        rowData?.controlPointDetails?.controlPoint || "Task"
      }`;
    } else if (rowData?.Status === "Pending" && newStatus === "Completed") {
      return `Add Remark for Completing Task - ${
        rowData?.controlPointDetails?.controlPoint || "Task"
      }`;
    }
    return `Add Remark for ${
      rowData?.controlPointDetails?.controlPoint || "Task"
    }`;
  };

  const getPlaceholder = () => {
    if (rowData?.Status === "Overdue" && newStatus === "Pending") {
      return "Explain why this task is being extended and set to pending...";
    } else if (rowData?.Status === "Overdue" && newStatus === "Completed") {
      return "Explain how you completed this overdue task...";
    } else if (rowData?.Status === "Pending" && newStatus === "Completed") {
      return "Add completion remarks...";
    }
    return "Enter your remark here...";
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogContent>
        {rowData?.Status === "Overdue" && newStatus === "Pending" && (
          <div
            style={{ marginBottom: "10px", color: "#666", fontSize: "14px" }}
          >
            <strong>Note:</strong> Due date will be extended to today.
          </div>
        )}
        <TextField
          autoFocus
          margin="dense"
          label="Remark"
          type="text"
          fullWidth
          variant="outlined"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={getPlaceholder()}
          multiline
          rows={3}
          onKeyPress={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleConfirm();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!comment.trim()}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function ControlPanel() {
  const { token } = useContext(AuthContext);
  const [selectedClient, setSelectedClient] = useState({});
  const [selectedOption, setSelectedOption] = useState("");
  const [accountResponsible, setAccountResponsible] = useState([]);
  const [selectedAccountResponsible, setSelectedAccountResponsible] = useState(
    []
  );
  const dropdownsearchRef = useRef(null);
  const dropdownRef = useRef(null);
  const {
    teamsData,
    setTeamsData,
    teamNames,
    setTeamNames,
    usersList,
    fetchTeams,
    usersLoading,
  } = useContext(UserContext);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [accSearch, setAccSearch] = useState("");
  const [accountResponsible2, setAccountResponsible2] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const gridRef = useRef();
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [loading, setLoading] = useState({ status: false, pstatus: false });
  const [error, setError] = useState(null);
  const [controlPoints, setControlPoints] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  const [selectedPrepareName, setSelectedPrepareName] = useState(null);
  const [teams, setTeams] = useState(
    teamNames?.filter(
      (team) =>
        team?.superTeamId === 0 && team?.type === "T" && team?.status === true
    )
  );

  // State for comment dialog
  const [commentDialog, setCommentDialog] = useState({
    open: false,
    rowData: null,
    newStatus: null,
    callback: null,
  });

  useEffect(() => {
    setTeams(
      teamNames?.filter(
        (team) =>
          team?.superTeamId === 0 && team?.type === "T" && team?.status === true
      )
    );
  }, [teamNames]);

  const [selectedTeams, setSelectedTeams] = useState([]);

  const { startDate, endDate } = useMemo(
    () => getMonthDateRange(year, month),
    [month, year]
  );

  useEffect(() => {
    setAccountResponsible(usersList);
    setAccountResponsible2(usersList);
  }, [usersList]);

  const getUserDetails = useMemo(() => {
    return {
      getName: (emId) => {
        if (!usersList || !emId) return "";
        const user = usersList.find((user) => user.EM_ID === emId);
        return user ? `${user.EM_FirstName} ${user.EM_LastName}` : "";
      },
      getTeam: (emId) => {
        if (!usersList || !teamNames || !emId) return "Unknown Team";
        const user = usersList.find((user) => user.EM_ID === emId);
        if (!user) return "Unknown Team";
        const team = teamNames.find(
          (team) => team.id === parseFloat(user.STM_Team)
        );
        return team?.name || "Unknown Team";
      },
    };
  }, [usersList, teamNames]);

  // Fetch control points
  const fetchControlPoints = useCallback(async () => {
    if (!token) return;
    setLoading((prev) => ({ ...prev, status: true }));
    setError(null);
    try {
      const response = await fetch(
        `${taskApiUrl}/GetControlPointByStatus?status=ALL`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      setControlPoints(data);
    } catch (error) {
      console.error("Error fetching control points:", error.message);
      setError(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, status: false }));
    }
  }, [token]);

  // Fetch mappings
  const fetchMappings = useCallback(async () => {
    setLoading((prev) => ({ ...prev, pstatus: true }));
    setError(null);
    try {
      const encodedStart = encodeURIComponent(startDate);
      const encodedEnd = encodeURIComponent(endDate);

      let emIdParam = "";
      if (selectedTeams.length > 0) {
        const teamEmployees = usersList
          .filter((user) => selectedTeams.includes(parseInt(user.STM_Team)))
          .map((user) => user.EM_ID);
        if (teamEmployees.length > 0)
          emIdParam = `&EMId=${teamEmployees.join(",")}`;
      } else if (selectedPrepareName) {
        emIdParam = `&EMId=${selectedPrepareName.EM_ID}`;
      }

      if (selectedStatus === "all") {
        const baseQuery = `StartDate=${encodedStart}&EndDate=${encodedEnd}${emIdParam}`;

        const completedResponse = await fetch(
          `${taskApiUrl}/ControlPointMappingbyDate?${baseQuery}&status=c`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const pendingResponse = await fetch(
          `${taskApiUrl}/ControlPointMappingbyDate?${baseQuery}&status=p`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!completedResponse.ok || !pendingResponse.ok) {
          throw new Error("Failed to fetch control point mappings");
        }

        const completedData = await completedResponse.json();
        const pendingData = await pendingResponse.json();

        const results = [
          ...(completedData.Result || []),
          ...(pendingData.Result || []),
        ];

        setMappings(results);
      } else {
        const apiStatus = selectedStatus === "c" ? "c" : "p";
        let queryParams = `StartDate=${encodedStart}&EndDate=${encodedEnd}&status=${apiStatus}${emIdParam}`;

        const response = await fetch(
          `${taskApiUrl}/ControlPointMappingbyDate?${queryParams}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) throw new Error(await response.text());
        const data = await response.json();

        let results = data.Result || [];

        if (selectedStatus === "o") {
          results = results.filter((item) => isBeforeToday(item.DueDate));
        } else if (selectedStatus === "p") {
          results = results.filter((item) => !isBeforeToday(item.DueDate));
        }

        setMappings(results);
      }
    } catch (error) {
      console.error("Error fetching mappings:", error);
      setError(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, pstatus: false }));
    }
  }, [
    startDate,
    endDate,
    selectedStatus,
    token,
    selectedTeams,
    selectedPrepareName,
    usersList,
  ]);

  // Merge controlPoints + mappings
  // Merge controlPoints + mappings - with Unknown Team filtering
  useEffect(() => {
    if (controlPoints.length > 0 && mappings.length > 0) {
      const merged = mappings
        .map((mapping) => {
          const controlPoint = controlPoints.find(
            (cp) => cp.id.toString() === mapping.ControlPointId.toString()
          );

          if (!controlPoint) return null;

          const mappingStatusRaw = (mapping.Status || "")
            .toString()
            .toLowerCase();
          const dueBeforeToday = isBeforeToday(mapping.DueDate);

          let displayStatus;
          if (mappingStatusRaw === "c" || mappingStatusRaw === "completed") {
            displayStatus = "Completed";
          } else {
            displayStatus = dueBeforeToday ? "Overdue" : "Pending";
          }

          // Get team name to check if it's "Unknown Team"
          const teamName = getUserDetails.getTeam(mapping.EmId);

          return {
            ...mapping,
            controlPointDetails: controlPoint,
            Status: displayStatus,
            teamName: teamName, // Add teamName for easy filtering
          };
        })
        .filter(Boolean)
        .filter((item) => item.teamName !== "Unknown Team"); // Filter out Unknown Team

      setCombinedData(merged);
    } else {
      setCombinedData([]);
    }
  }, [controlPoints, mappings, selectedStatus, getUserDetails]);

  useEffect(() => {
    fetchControlPoints();
  }, [fetchControlPoints]);

  useEffect(() => {
    if (controlPoints.length > 0) {
      fetchMappings();
    }
  }, [
    fetchMappings,
    controlPoints.length,
    startDate,
    endDate,
    selectedStatus,
    selectedTeams,
    selectedPrepareName,
  ]);

  useEffect(() => {
    if (gridRef.current?.api && usersList && combinedData.length > 0) {
      gridRef.current.api.refreshCells({ force: true });
    }
  }, [usersList, combinedData]);

  // Handle status change
  const handleStatusChange = useCallback(
    async (rowData, newStatus, comment = null) => {
      if (!token) {
        setError("Authentication token not available");
        return false;
      }

      try {
        const currentDate = new Date().toISOString().split(".")[0];
        const originalDueDate = rowData.DueDate;

        // Calculate new DueDate - if changing from Overdue to Pending, set to today
        let newDueDate = rowData.DueDate;
        if (rowData.Status === "Overdue" && newStatus === "Pending") {
          // Set DueDate to today when changing from Overdue to Pending
          const today = new Date();
          today.setHours(23, 59, 59, 999); // End of day
          newDueDate = today.toISOString().split(".")[0];
        }

        const payload = {
          Id: rowData.Id.toString(),
          EmpployeeId: rowData.EmId,
          ControlPointId: rowData.ControlPointId.toString(),
          Status:
            newStatus === "Completed"
              ? "c" // Send 'c' for Completed
              : newStatus === "Pending"
              ? "p" // Send 'p' for Pending
              : "",
          AcceptedDate: newStatus === "Completed" ? currentDate : null,
          // DueDate: newDueDate, // Include the updated DueDate
          Comment: comment || "", // Always include comment if provided
        };

        const response = await fetch(
          `${taskApiUrl}/UpdateControlPointMapping`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || "Failed to update status");
        }

        setMappings((prevMappings) =>
          prevMappings.map((mapping) =>
            (mapping.Id?.toString() || "") === (rowData.Id?.toString() || "")
              ? {
                  ...mapping,
                  Status: newStatus,
                  AcceptedDate: newStatus === "Completed" ? currentDate : null,
                  DueDate: newDueDate, // Update DueDate in state
                  Comment: comment || "",
                }
              : mapping
          )
        );

        return true;
      } catch (error) {
        console.error("Error updating control point status:", error);
        setError(`Failed to update status: ${error.message}`);
        return false;
      }
    },
    [token]
  );
  // Open comment dialog
  const openCommentDialog = (rowData, newStatus, callback) => {
    setCommentDialog({
      open: true,
      rowData,
      newStatus,
      callback,
    });
  };

  // Close comment dialog
  const closeCommentDialog = () => {
    setCommentDialog({
      open: false,
      rowData: null,
      newStatus: null,
      callback: null,
    });
  };

  // Handle comment confirmation
  const handleCommentConfirm = async (comment) => {
    const { rowData, newStatus, callback } = commentDialog;

    if (rowData && newStatus && callback) {
      const success = await callback(rowData, newStatus, comment);
      if (success) {
        closeCommentDialog();
      }
    }
  };

  // Status cell renderer - updated to use Material-UI dialog
  // Status cell renderer - updated to show both Pending and Completed for Overdue tasks
  const StatusDropdownRenderer = (params) => {
    const { onStatusChange } = params;
    const [status, setStatus] = useState(params.value);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
      setStatus(params.value);
    }, [params.value]);

    const handleChange = async (event) => {
      const newStatus = event.target.value;
      const originalStatus = status;

      // Handle Overdue → Pending (requires comment)
      if (originalStatus === "Overdue" && newStatus === "Pending") {
        openCommentDialog(
          params.data,
          newStatus,
          async (rowData, status, comment) => {
            setIsUpdating(true);
            try {
              const success = await onStatusChange(rowData, status, comment);
              if (success) {
                setStatus(status);
                params.api.refreshCells({
                  rowNodes: [params.node],
                  columns: ["Status", "AcceptedDate", "DueDate"],
                });
                return true;
              } else {
                setStatus(originalStatus);
                return false;
              }
            } catch (error) {
              setStatus(originalStatus);
              console.error("Error updating status:", error);
              return false;
            } finally {
              setIsUpdating(false);
            }
          }
        );
      }
      // Handle Overdue → Completed (requires comment)
      else if (originalStatus === "Overdue" && newStatus === "Completed") {
        openCommentDialog(
          params.data,
          newStatus,
          async (rowData, status, comment) => {
            setIsUpdating(true);
            try {
              const success = await onStatusChange(rowData, status, comment);
              if (success) {
                setStatus(status);
                params.api.refreshCells({
                  rowNodes: [params.node],
                  columns: ["Status", "AcceptedDate"],
                });
                return true;
              } else {
                setStatus(originalStatus);
                return false;
              }
            } catch (error) {
              setStatus(originalStatus);
              console.error("Error updating status:", error);
              return false;
            } finally {
              setIsUpdating(false);
            }
          }
        );
      }
      // Handle Pending → Completed (no comment required)
      else if (originalStatus === "Pending" && newStatus === "Completed") {
        setIsUpdating(true);
        try {
          const success = await onStatusChange(params.data, newStatus, null);
          if (success) {
            setStatus(newStatus);
            params.api.refreshCells({
              rowNodes: [params.node],
              columns: ["Status", "AcceptedDate"],
            });
          } else {
            setStatus(originalStatus);
          }
        } catch (error) {
          setStatus(originalStatus);
          console.error("Error updating status:", error);
        } finally {
          setIsUpdating(false);
        }
      }
    };

    // Render dropdowns based on status
    if (status === "Overdue") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <select
            value={status}
            onChange={handleChange}
            disabled={isUpdating}
            style={{
              padding: "4px 8px",
              fontSize: "12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              outline: "none",
              minWidth: "140px",
              backgroundColor: isUpdating ? "#f5f5f5" : "white",
            }}
          >
            <option value="Overdue">Overdue</option>

            <option value="Completed">Mark as Completed</option>
          </select>
          {isUpdating && (
            <div style={{ fontSize: "12px", color: "#666" }}>Updating...</div>
          )}
        </div>
      );
    }

    if (status === "Pending") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <select
            value={status}
            onChange={handleChange}
            disabled={isUpdating}
            style={{
              padding: "4px 8px",
              fontSize: "12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              outline: "none",
              minWidth: "140px",
              backgroundColor: isUpdating ? "#f5f5f5" : "white",
            }}
          >
            <option value="Pending">Pending</option>
            <option value="Completed">Mark as Completed</option>
          </select>
          {isUpdating && (
            <div style={{ fontSize: "12px", color: "#666" }}>Updating...</div>
          )}
        </div>
      );
    }

    // For Completed
    return (
      <div
        style={{
          padding: "4px 8px",
          color: status === "Completed" ? "green" : "red",
          fontWeight: status === "Overdue" ? "bold" : "normal",
        }}
      >
        {status}
      </div>
    );
  };

  const columnDefs = useMemo(
    () => [
      { field: "Id", headerName: "ID", width: 80 },
      { field: "ControlPointId", headerName: "CP ID", width: 80 },
      {
        field: "CreatedDate",
        headerName: "Created Date",
        valueFormatter: (params) => params.value?.split("T")[0] || "",
        width: 120,
      },
      {
        headerName: "Name",
        valueGetter: (params) => getUserDetails.getName(params.data.EmId),
        width: 120,
      },
      {
        headerName: "Team Name",
        valueGetter: (params) => getUserDetails.getTeam(params.data.EmId),
        width: 120,
      },
      {
        field: "DueDate",
        headerName: "Due Date",
        valueFormatter: (params) => params.value?.split("T")[0] || "",
        width: 120,
      },
      {
        field: "AcceptedDate",
        headerName: "Accepted Date",
        valueFormatter: (params) => params.value?.split("T")[0] || "",
        width: 120,
      },
      {
        headerName: "Control Point",
        valueGetter: (params) =>
          params.data.controlPointDetails?.controlPoint || "",
        width: 150,
      },
      {
        headerName: "Key Controls For",
        valueGetter: (params) =>
          params.data.controlPointDetails?.keycontrolsfor || "",
        width: 150,
      },
      {
        headerName: "Description",
        valueGetter: (params) =>
          params.data.controlPointDetails?.controlDescription || "",
        width: 200,
      },
      {
        headerName: "TAT",
        valueGetter: (params) => params.data.controlPointDetails?.tat || "",
        width: 80,
      },
      {
        headerName: "Accuracy",
        valueGetter: (params) =>
          params.data.controlPointDetails?.accuracy || "",
        width: 100,
      },
      {
        headerName: "Frequency",
        valueGetter: (params) =>
          params.data.controlPointDetails?.frequency || "",
        width: 120,
      },
      {
        headerName: "Criticality",
        valueGetter: (params) =>
          params.data.controlPointDetails?.criticality || "",
        width: 120,
      },
      {
        headerName: "Responsible",
        valueGetter: (params) =>
          params.data.controlPointDetails?.controlResponsible || "",
        width: 150,
      },
      {
        field: "Status",
        headerName: "Status",
        width: 150,
        cellRenderer: StatusDropdownRenderer,
        cellRendererParams: {
          onStatusChange: handleStatusChange,
          getUserDetails: getUserDetails,
        },
        editable: false,
      },
      {
        field: "Comment",
        headerName: "Remarks",
        valueFormatter: (params) => params.value?.split("T")[0] || "",
        width: 120,
      },
    ],
    [getUserDetails, handleStatusChange]
  );

  const handleDownload = async () => {
    try {
      const XLSX = await import("xlsx");
      const wb = XLSX.utils.book_new();
      const rowData = combinedData.map((item) => ({
        ID: item.Id,
        "CP ID": item.ControlPointId,
        "Created Date": item.CreatedDate?.split("T")[0] || "",
        Name: getUserDetails.getName(item.EmId),
        "Team Name": getUserDetails.getTeam(item.EmId),
        "Due Date": item.DueDate?.split("T")[0] || "",
        "Accepted Date":
          item.AcceptedDate === "0001-01-01T00:00:00"
            ? ""
            : item.AcceptedDate?.split("T")[0] || "",
        "Control Point": item.controlPointDetails?.controlPoint || "",
        "Key Controls For": item.controlPointDetails?.keycontrolsfor || "",
        Description: item.controlPointDetails?.controlDescription || "",
        TAT: item.controlPointDetails?.tat || "",
        Accuracy: item.controlPointDetails?.accuracy || "",
        Frequency: item.controlPointDetails?.frequency || "",
        Criticality: item.controlPointDetails?.criticality || "",
        Responsible: item.controlPointDetails?.controlResponsible || "",
        Status: item.Status,
        Remarks: item.Comment || "", // ✅ Fixed: Directly use the comment without date formatting
      }));

      const ws = XLSX.utils.json_to_sheet(rowData);
      const colWidths = [
        { wch: 8 },
        { wch: 8 },
        { wch: 12 },
        { wch: 20 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
        { wch: 20 },
        { wch: 20 },
        { wch: 30 },
        { wch: 8 },
        { wch: 10 },
        { wch: 12 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
        { wch: 30 }, // ✅ Added width for Remarks column
      ];
      ws["!cols"] = colWidths;
      XLSX.utils.book_append_sheet(wb, ws, "Control Points");

      const date = new Date();
      const fileName = `ControlPoints_${
        selectedStatus === "all"
          ? "All"
          : selectedStatus === "p"
          ? "Pending"
          : selectedStatus === "c"
          ? "Completed"
          : "Overdue"
      }_${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}.xlsx`;

      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      setError("Failed to export data. Please try again.");
    }
  };

  const handleCheckboxAccountResponsibleChange = (acc) => {
    setSelectedOption({});

    if (selectedPrepareName?.EM_ID === acc.EM_ID) {
      setSelectedPrepareName(null);
      setSelectedAccountResponsible([]);
      setSelectedEmployees([]);
      setSelectedTeams([]);
      return;
    }

    if (selectedTeams?.length) {
      setSelectedTeams([]);
    }

    setSelectedAccountResponsible([acc]);
    setSelectedPrepareName(acc);
    setSelectedEmployees([acc]);
    setSelectedClient([]);
    setOpenDropdown(null);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownsearchRef.current &&
        !dropdownsearchRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (error) {
    return (
      <div>
        <NavSub />
        <div
          className="error-message"
          style={{ padding: "20px", color: "red" }}
        >
          Error: {error}
        </div>
      </div>
    );
  }

  if (loading.status || loading.pstatus || usersLoading) {
    return (
      <div>
        <NavSub />
        <div className="loading-message" style={{ padding: "20px" }}>
          Loading data...
        </div>
      </div>
    );
  }

  return (
    <div>
      <NavSub />

      {/* Comment Dialog */}
      <CommentDialog
        open={commentDialog.open}
        onClose={closeCommentDialog}
        onConfirm={handleCommentConfirm}
        rowData={commentDialog.rowData}
        newStatus={commentDialog.newStatus}
      />

      <div className="control-panel">
        <div className="flex gap-4 m-4" style={{ alignItems: "center" }}>
          {/* ... rest of your JSX remains the same ... */}
          <div
            className="Title-STR-main2"
            onClick={() => {
              setOpenDropdown(openDropdown === "teams" ? null : "teams");
            }}
          >
            {selectedTeams.length > 0
              ? teams
                  .filter((team) => selectedTeams.includes(team.id))
                  .map((team) => team.name)
                  .join(", ")
              : "Select Team"}
            <FontAwesomeIcon
              className="str-caret-icon"
              icon={openDropdown === "teams" ? faCaretUp : faCaretDown}
            />

            {openDropdown === "teams" && (
              <div ref={dropdownRef} className="search-drop-DailyVoucher">
                <div
                  className="search-drop-page"
                  style={{ marginLeft: "25px" }}
                >
                  <div
                    className="main-suggestion"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    <div
                      style={{ color: "red", padding: "2px 22px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTeams([]);
                      }}
                    >
                      Clear All
                    </div>

                    {teams.map((team) => (
                      <div key={team.id} className="suggestion-list-li">
                        <input
                          type="checkbox"
                          className="radio-searchbox"
                          style={{
                            accentColor: "rgb(80, 20, 90)",
                            width: "18px",
                            height: "18px",
                          }}
                          checked={selectedTeams.includes(team.id)}
                          onChange={() => {
                            const isSelected = selectedTeams.includes(team.id);
                            if (isSelected) {
                              setSelectedTeams(
                                selectedTeams.filter((id) => id !== team.id)
                              );
                            } else {
                              setSelectedTeams([...selectedTeams, team.id]);
                            }
                          }}
                        />
                        <label className="li-content">{team.name}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className="Title-STR-main2"
            onClick={() => {
              setOpenDropdown(openDropdown === "preparer" ? null : "preparer");
            }}
          >
            {selectedPrepareName
              ? `${selectedPrepareName.EM_FirstName} ${selectedPrepareName.EM_LastName}`
              : "Preparer Name"}
            <FontAwesomeIcon
              className="str-caret-icon"
              icon={openDropdown === "preparer" ? faCaretUp : faCaretDown}
            />
            {openDropdown === "preparer" && (
              <div ref={dropdownsearchRef} className="search-drop-DailyVoucher">
                <div className="search-drop-page">
                  <div
                    className="main-suggestion"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    <div className="input-suggestion">
                      <div className="icon-search-phospher">
                        <MagnifyingGlass
                          size={20}
                          color="#ada4a4"
                          weight="fill"
                        />
                      </div>
                      <input
                        type="text"
                        value={accSearch}
                        className="input-suggestion-search"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const value = e.target.value.toLowerCase();
                          setAccSearch(value);
                          setAccountResponsible2(
                            accountResponsible?.filter((acc) =>
                              (acc.EM_FirstName + " " + acc?.EM_LastName)
                                .toLowerCase()
                                .includes(value)
                            )
                          );
                        }}
                        placeholder="Start typing..."
                      />
                    </div>
                    <div tabIndex={1}>
                      <div
                        style={{ color: "red", padding: "2px 22px" }}
                        onClick={() => {
                          setSelectedPrepareName(null);
                          setSelectedAccountResponsible([]);
                          setSelectedEmployees([]);
                        }}
                      >
                        Clear All
                      </div>
                      {accountResponsible2?.map((acc) => (
                        <div key={acc.EM_ID} className="suggestion-list-li">
                          <input
                            type="radio"
                            className="radio-searchbox"
                            style={{
                              accentColor: "rgb(80, 20, 90)",
                              width: "18px",
                              height: "18px",
                            }}
                            checked={selectedPrepareName?.EM_ID === acc.EM_ID}
                            onChange={() =>
                              handleCheckboxAccountResponsibleChange(acc)
                            }
                          />
                          <label className="li-content">
                            {acc.EM_FirstName + " " + acc.EM_LastName}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={{ position: "relative" }}>
            <input
              type="month"
              value={`${year}-${String(month + 1).padStart(2, "0")}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split("-");
                setYear(Number(y));
                setMonth(Number(m) - 1);
                setOpenDropdown(null);
              }}
              onFocus={() => setOpenDropdown("month")}
              onBlur={() => setOpenDropdown(null)}
              style={{
                padding: "8px 12px",
                fontSize: "14px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                outline: "none",
                margin: "5px 0",
              }}
            />
          </div>

          <label style={{ marginLeft: "10px", fontWeight: "500" }}>
            Status:{" "}
          </label>
          <StatusFilter
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          />
          <div className="cust_common_btn" onClick={handleDownload}>
            <Download weight="fill" size={20} /> DOWNLOAD
          </div>
        </div>

        <div
          className="ag-theme-alpine"
          style={{
            width: "100%",
            height: "calc(100vh - 200px)",
            marginTop: "20px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <AgGridReact
            ref={gridRef}
            rowData={combinedData}
            columnDefs={columnDefs}
            domLayout="normal"
            getRowHeight={() => 48}
            headerHeight={60}
            suppressCellFocus={true}
            suppressRowClickSelection={true}
            animateRows={true}
            defaultColDef={{
              resizable: true,
              sortable: true,
              filter: true,
            }}
            suppressScrollOnNewData={true}
            alwaysShowHorizontalScroll={true}
            alwaysShowVerticalScroll={true}
            context={{
              onStatusChange: handleStatusChange,
              getUserDetails: getUserDetails,
            }}
            gridOptions={{
              getRowStyle: () => ({ position: "relative" }),
              getHeaderRowStyle: () => ({
                position: "sticky",
                top: 0,
                zIndex: 100,
                backgroundColor: "white",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
              }),
            }}
          />
        </div>
      </div>
    </div>
  );
}
